import { useMemo } from 'react';

interface MessageBodyProps {
  readonly html: string | null;
  readonly text: string | null;
}

/**
 * Renders a message body.
 *
 * HTML mail is hostile input: it is written by whoever sent it, and this app's
 * whole purpose is receiving mail from strangers. It is therefore never
 * injected into the page. It goes into a sandboxed iframe with `srcdoc`, which
 * gives it an opaque origin and no script execution, so it cannot read the
 * session token out of localStorage, cannot call the API as us, and cannot
 * touch the surrounding document.
 *
 * The two sandbox tokens that are granted let a link open in a new tab —
 * clicking the confirmation link is usually the entire reason the message was
 * wanted. Notably absent is `allow-scripts`, and it must stay absent:
 * `allow-scripts` together with `allow-same-origin` would let the frame remove
 * its own sandbox attribute and defeat the whole arrangement.
 */
export function MessageBody({ html, text }: MessageBodyProps) {
  const document = useMemo(() => (html === null ? null : wrap(html)), [html]);

  if (document !== null) {
    return (
      <iframe
        className="reader__frame"
        title="Message content"
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={document}
        // Referrer suppressed so that merely opening a message does not tell
        // every tracking pixel in it which page it was viewed from.
        referrerPolicy="no-referrer"
        style={{ height: '100%' }}
      />
    );
  }

  if (text) return <pre className="reader__text">{text}</pre>;

  return (
    <div className="empty">
      <span className="empty__title">Empty message</span>
      <span className="empty__hint">This message arrived with no readable body.</span>
    </div>
  );
}

/**
 * Wraps a mail body in a minimal document.
 *
 * `<base target="_blank">` makes every link open in a new tab, which is the
 * only way a link in a sandboxed frame can go anywhere at all. The white
 * background is deliberate: HTML mail is authored against white, and a lot of
 * it sets dark text without setting a background, so rendering it on the app's
 * near-black ground leaves half of it invisible.
 */
function wrap(html: string): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<base target="_blank">
<style>
  html, body { margin: 0; background: #ffffff; }
  body {
    padding: 20px 24px;
    color: #16181d;
    font: 14px/1.6 ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    overflow-wrap: break-word;
  }
  img { max-width: 100%; height: auto; }
  a { color: #0a7d4b; }
  table { max-width: 100%; }
</style>
</head>
<body>${html}</body>
</html>`;
}
